// Common types
export interface IObserver {
  closed?: boolean;
  next(value: IServiceEvent): void;
  error(error: Error): void;
  complete(): void;
}

export interface IObservable {
  subscribe(observer: IObserver): void;
  publish(event: IServiceEvent): void;
}

export interface IABAObserver extends Omit<IObserver, 'error'> {
  error(error: IServiceErrorEvent): void;
}

export interface IABAObservable extends Omit<IObservable, 'subscribe'> {
  subscribe(observer: IABAObserver): void;
}

export interface StringMap<T> {
  [key: string]: T;
}

// Custom event extended types
export interface LoadedCustomEvent extends CustomEvent {
  detail: {
    name: ServiceName;
  };
}

export interface RegisteredCustomEvent extends LoadedCustomEvent {
  detail: {
    name: ServiceName;
    observable: IABAObservable;
  };
}

// Service event payload
type ServiceEventSuccessPayload = { data: string } | StringMap<any>;
type ServiceEventErrorPayload = { error: Error };
export type ServiceEventPayload =
  | ServiceEventSuccessPayload
  | ServiceEventErrorPayload;

// service event fields
export type ServiceName = string;
export type ServiceEventId = string;
export enum ServiceEventTypes {
  Event = 'event',
  RequestData = 'request:data',
  RequestModal = 'request:modal',
  RequestNotification = 'request:notification',
}

export interface IServiceEvent {
  type: ServiceEventTypes;
  scope: ServiceName;
  id: ServiceEventId;
  payload?: ServiceEventPayload;
}

export interface IServiceErrorEvent {
  type: ServiceEventTypes;
  scope: ServiceName;
  id: ServiceEventId;
  payload: ServiceEventErrorPayload;
}

export enum InternalServiceEventType {
  Common = '*',
  Loaded = 'service:loaded',
  Registered = 'service:registered',
  Initialize = 'service:initialize',
  Render = 'service:render',
  Destroy = 'service:destroy',
}

export type InternalServiceEventTypeCallback = (event?: IServiceEvent) => void;

export type InternalServiceEventMap = {
  [key in InternalServiceEventType]: Array<InternalServiceEventTypeCallback>;
};

export interface IAssetBasedService {
  name: string;

  // lifecycle methods
  onRegistered(callback: InternalServiceEventTypeCallback): void;
  onInitialize(callback: InternalServiceEventTypeCallback): void;
  onRender(callback: InternalServiceEventTypeCallback): void;
  onDestroy(callback: InternalServiceEventTypeCallback): void;
  onMessage(callback: InternalServiceEventTypeCallback): () => void;

  start(): void;
  stop(): void;

  publish(
    type: ServiceEventTypes,
    id: ServiceEventId,
    payload?: ServiceEventPayload
  ): void;
}

export interface Logger {
  error(...args: any): void;
  warn(...args: any): void;
  log(...args: any): void;
  info(...args: any): void;
}
